// Ensure cartItemData is globally defined
// NOTE: We key cartItemData by Shopify line item key (item.key), not variant_id,
// so subscription + one-time variants can exist as separate visible lines.
let cartItemData = {};

// ==============================
// Cart Debugging
// ==============================
const CART_DEBUG = true;
function cartLog(...args) {
  if (!CART_DEBUG) return;
  console.log('[CartDebug]', ...args);
}
function cartGroup(label, fn) {
  if (!CART_DEBUG) return fn();
  console.groupCollapsed(`[CartDebug] ${label}`);
  try {
    return fn();
  } finally {
    console.groupEnd();
  }
}


class DropdownManager {
  constructor() {
    this.navItems = document.querySelectorAll('.site-nav .first-list-item.megadropdown > a');
    this.activeDropdown = null;  // Track the currently active dropdown
    this.init();
  }

  init() {
    this.navItems.forEach((navItem) => {
      const dropdown = navItem.nextElementSibling;

      // Show dropdown on focus or hover
      navItem.addEventListener('focus', () => this.showDropdown(dropdown, navItem));
      navItem.addEventListener('mouseenter', () => this.showDropdown(dropdown, navItem));

      // Keep dropdown open while navigating within it
      dropdown.addEventListener('focusin', () => this.showDropdown(dropdown, navItem));

      // Hide dropdown on blur, mouseleave, or tabbing out
      navItem.addEventListener('blur', (e) => this.hideDropdownOnBlurOrMouseLeave(e, dropdown, navItem));
      dropdown.addEventListener('focusout', (e) => this.hideDropdownOnBlurOrMouseLeave(e, dropdown, navItem));
      navItem.addEventListener('mouseleave', () => this.hideDropdown(dropdown, navItem));
      dropdown.addEventListener('mouseleave', () => this.hideDropdown(dropdown, navItem));
    });

    // Listen for Escape key globally
    document.addEventListener('keydown', (e) => this.handleGlobalKeydown(e));
  }

  showDropdown(dropdown, navItem) {
    this.activeDropdown = { dropdown, navItem };  // Track the active dropdown and nav item
    dropdown.classList.add('active');
    navItem.setAttribute('aria-expanded', 'true');
  }

  hideDropdown(dropdown, navItem) {
    this.activeDropdown = null;  // Clear the active dropdown
    dropdown.classList.remove('active');
    navItem.setAttribute('aria-expanded', 'false');
  }

  handleGlobalKeydown(e) {
    if (e.key === 'Escape' && this.activeDropdown) {
      const { dropdown, navItem } = this.activeDropdown;
      this.hideDropdown(dropdown, navItem);
      navItem.focus();  // Return focus to the nav item
    }
  }

  hideDropdownOnBlurOrMouseLeave(e, dropdown, navItem) {
    setTimeout(() => {
      // Only hide if focus is outside both the link and the dropdown
      if (!navItem.contains(document.activeElement) && !dropdown.contains(document.activeElement)) {
        this.hideDropdown(dropdown, navItem);
      }
    }, 10);
  }
}

// Initialize DropdownManager
new DropdownManager();

class CookieManager {
  static setCookie(name, value, days) {
    let expires = "";
    if (days) {
      let date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  static getCookie(name) {
    let nameEQ = name + "=";
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  static updateUserData(name, gender, size, days) {
    let cookieValue = this.getCookie(name);
    let cookieData = cookieValue ? JSON.parse(cookieValue) : { gender: gender, genderHistory: '', sizeHistory: '' };

    // Update genderHistory
    if (!cookieData['genderHistory']) {
      cookieData['genderHistory'] = [];
    } else {
      cookieData['genderHistory'] = cookieData['genderHistory'].split(',');
    }
    cookieData['genderHistory'].push(gender);
    if (cookieData['genderHistory'].length > 20) {
      cookieData['genderHistory'].shift();
    }

    // Update sizeHistory
    if (!cookieData['sizeHistory']) {
      cookieData['sizeHistory'] = [];
    } else {
      cookieData['sizeHistory'] = cookieData['sizeHistory'].split(',');
    }
    cookieData['sizeHistory'].push(size);
    if (cookieData['sizeHistory'].length > 20) {
      cookieData['sizeHistory'].shift();
    }

    // Calculate majority gender and size
    cookieData['gender'] = this.calculateMajority(cookieData['genderHistory']);
    cookieData['size'] = this.calculateMajority(cookieData['sizeHistory'], true);

    // Convert history arrays back to strings
    cookieData['genderHistory'] = cookieData['genderHistory'].join(',');
    cookieData['sizeHistory'] = cookieData['sizeHistory'].join(',');

    // Update the cookie with the final values
    this.setCookie(name, JSON.stringify(cookieData), days);
  }

  // Calculate the majority in history arrays
  static calculateMajority(historyArray, isSize = false) {
    if (historyArray.length <= 3) return null; // Not enough entries to calculate majority

    let count = historyArray.reduce((acc, item) => {
      acc[item] = (acc[item] || 0) + 1;
      return acc;
    }, {});

    let twoThirds = Math.ceil((2 / 3) * historyArray.length);
    let majorityItem = null;

    for (let item in count) {
      if (count[item] >= twoThirds) {
        majorityItem = item;
        break;
      }
    }

    return majorityItem;
  }
}

// Get shipping limit from theme settings
let shippingLimit = document.querySelector('.ocs-minicart').getAttribute('data-shipping-limit');
shippingLimit = parseFloat(shippingLimit).toFixed(2);

// Debounce function
function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        try {
          const result = fn.apply(this, args);
          if (result instanceof Promise) {
            result.then(resolve).catch(reject);
          } else {
            resolve(result);
          }
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
}

// Update header cart count function
function updateHeaderCartCount(itemCount) {
  document.querySelectorAll('.cart .count').forEach(headerCartCount => {
    if (headerCartCount) {
      headerCartCount.textContent = itemCount === 0 ? '' : itemCount;
    }
    if (itemCount <= 0) {
      headerCartCount.classList.add('empty');
    } else {
      headerCartCount.classList.remove('empty');
    }
  });
}

// Global function to fetch cart data and update the mini-cart
function fetchCartData() {
  cartLog('fetchCartData() start');
  return fetch('/cart.js')
    .then(response => response.json())
    .then(cart => cartGroup('Fetched /cart.js', () => {
      cartLog('Cart summary', {
        item_count: cart.item_count,
        total_price: cart.total_price,
        total_discount: cart.total_discount,
        lines: cart.items ? cart.items.length : 0
      });
      if (Array.isArray(cart.items) && cart.items.length) {
        cartLog('Cart lines', cart.items.map(i => ({
          key: i.key,
          variant_id: i.variant_id,
          quantity: i.quantity,
          selling_plan: i.selling_plan_allocation?.selling_plan?.name || null
        })));
      }
      return cart;
    }))
    .then(cart => {
      let cartBody = document.querySelector('.cart-body');
      let cartFooter = document.querySelector('.cart-footer');
      let cartSummary = document.querySelector('.cart-summary');
      let shippingHeaderMessage = document.querySelector('.shipping-header-message');
      let emptyCartContent = document.querySelector('.empty-cart-content');
      let headerCartCount = document.querySelector('.header-cart-count');
      let shippingBarProgress = document.querySelector('.shipping-bar-progress');
      let shippingBar = document.querySelector('.bar');
      let cartDiscountLine = document.querySelector('.discount-ammount');

      // Fetch the wh_user cookie and parse it to get the gender value
      let dpUserCookie = CookieManager.getCookie('wh_user');
      let genderSetting = dpUserCookie ? JSON.parse(dpUserCookie).gender : null;

      if (genderSetting == 'M') {
        document.querySelectorAll('.mens-upsell-collection').forEach(x => { x.style.display = 'flex'; });
        document.querySelectorAll('.womens-upsell-collection').forEach(x => { x.style.display = 'none'; });
        document.querySelectorAll('.unisex-upsell-collection').forEach(x => { x.style.display = 'none'; });
      } else if (genderSetting == 'W') {
        document.querySelectorAll('.mens-upsell-collection').forEach(x => { x.style.display = 'none'; });
        document.querySelectorAll('.womens-upsell-collection').forEach(x => { x.style.display = 'flex'; });
        document.querySelectorAll('.unisex-upsell-collection').forEach(x => { x.style.display = 'none'; });
      } else {
        document.querySelectorAll('.mens-upsell-collection').forEach(x => { x.style.display = 'none'; });
        document.querySelectorAll('.womens-upsell-collection').forEach(x => { x.style.display = 'none'; });
        document.querySelectorAll('.unisex-upsell-collection').forEach(x => { x.style.display = 'flex'; });
      }

      // Check if the cart is empty
      if (!cart.items || cart.items.length === 0) {
        if (cartBody) cartBody.classList.add('empty');
        if (emptyCartContent) emptyCartContent.style.display = 'block';
        if (cartFooter) cartFooter.style.display = 'none';
        if (cartSummary) cartSummary.style.display = 'none';
        if (headerCartCount) headerCartCount.innerHTML = '(0 Items)';
        if (shippingHeaderMessage) shippingHeaderMessage.style.display = 'none';
        if (shippingBarProgress) shippingBarProgress.style.width = '0%';
        if (shippingBar) shippingBar.style.display = 'none';

        updateHeaderCartCount(0);
        cartItemData = {};
        renderCartItems();
        return cart;
      } else {
        if (cartBody) cartBody.classList.remove('empty');
        if (cartSummary) cartSummary.style.display = 'block';
        if (emptyCartContent) emptyCartContent.style.display = 'none';
        if (shippingBar) shippingBar.style.display = 'block';
        if (shippingHeaderMessage) shippingHeaderMessage.style.display = 'block';
        if (cartFooter) cartFooter.style.display = 'block';
      }

      // Rebuild cartItemData keyed by line item key
      const nextCartItemData = {};
      cart.items.forEach((item, index) => {
        const lineKey = item.key;
        const isSubscription = !!item.selling_plan_allocation;

        nextCartItemData[lineKey] = {
          key: lineKey,
          variantId: item.variant_id,
          num: index,
          id: `CartItem-${index + 1}`,
          shortname: item.product_title,
          price: item.price,
          inv: item.inventory_quantity,
          featured_image: item.featured_image,
          url: item.url,
          title: item.product_title,
          options: item.options_with_values.map(option => ({
            name: option.name,
            value: option.value
          })),
          discounts: item.discounts.map(discount => discount.title),
          quantity: item.quantity,
          linePrice: (item.line_price / 100).toFixed(2),
          originalLinePrice: (item.original_line_price / 100).toFixed(2),
          subscription: isSubscription,
          subscriptionName: isSubscription ? item.selling_plan_allocation.selling_plan.name : null
        };
      });

      cartItemData = nextCartItemData;
      cartGroup('Rebuilt cartItemData', () => {
        cartLog('keys', Object.keys(cartItemData));
      });

      let newTotalDiscount = (cart.total_discount / 100).toFixed(2);
      if (cartDiscountLine) cartDiscountLine.textContent = '-$' + newTotalDiscount;

      let newCartTotal = (cart.total_price / 100).toFixed(2);
      const cartTotalEl = document.querySelector('.cart-total-value');
      if (cartTotalEl) cartTotalEl.textContent = '$' + newCartTotal;
      if (headerCartCount) headerCartCount.innerHTML = `(${cart.item_count} Item${cart.item_count > 1 ? 's' : ''})`;

      updateShipping(newCartTotal, shippingLimit);
      updateHeaderCartCount(cart.item_count);

      renderCartItems();
      cartLog('fetchCartData() done');
      return cart;
    })
    .catch(error => console.error('[CartDebug] Error fetching cart:', error));
}

// Function to update shipping progress
function updateShipping(cartTotal, shippingLimit) {
  shippingLimit = parseFloat(shippingLimit).toFixed(2); // Ensure it is a number with two decimal places
  let freeShippingPercent = ((cartTotal / shippingLimit) * 100).toFixed(0);
  let progressBar = document.querySelector(".shipping-bar-progress");
  let shippingSummaryText = document.querySelector(".shipping-type");
  let shippingHeader = document.querySelector(".shipping-header-message");
  if (freeShippingPercent >= 100) {
    freeShippingPercent = 100;
  }
  progressBar.style.width = freeShippingPercent + "%";
  if (freeShippingPercent >= 100) {
    shippingSummaryText.innerHTML = "<span>Free Shipping!</span>";
    shippingHeader.innerHTML = "YOU'VE EARNED FREE SHIPPING!";
  } else {
    shippingSummaryText.textContent = "Calculated in Checkout";
    if (freeShippingPercent == 0) {
      shippingHeader.innerHTML = "FREE SHIPPING ON ORDERS $" + shippingLimit + " OR MORE.";
    } else {
      let neededAmount = (shippingLimit - cartTotal).toFixed(2);
      shippingHeader.innerHTML = "ADD $" + neededAmount + " OR MORE TO GET FREE SHIPPING!";
    }
  }
}

// Function to capture existing cart items and store their data
function captureCartItems() {
  cartLog('captureCartItems() start');
  document.querySelectorAll('.cart-item').forEach(cartItem => {
    const lineKey = cartItem.getAttribute('data-key');
    const variantId = cartItem.getAttribute('data-id');
    const keyToUse = lineKey || `__placeholder__${variantId || Math.random()}`;

    let imageElement = cartItem.querySelector('.item-image img');
    let imageSrc = imageElement ? imageElement.src : '';
    let imageAlt = imageElement ? imageElement.alt : '';
    let imageAspectRatio = imageElement ? (imageElement.naturalWidth / imageElement.naturalHeight) : 1;
    let itemNameElement = cartItem.querySelector('.item-name');
    let itemName = itemNameElement ? itemNameElement.textContent.trim() : '';

    const itemPriceElement = cartItem.querySelector('.item-price');
    const endPriceElement = cartItem.querySelector('.price--end');
    let originalLinePrice = '';

    if (itemPriceElement) {
      originalLinePrice = itemPriceElement.textContent.replace('$', '');
    } else if (endPriceElement) {
      originalLinePrice = endPriceElement.textContent.replace('$', '');
    }

    cartItemData[keyToUse] = {
      key: lineKey || null,
      variantId: variantId || null,
      num: cartItem.getAttribute('data-num'),
      id: cartItem.getAttribute('id'),
      shortname: cartItem.getAttribute('data-shortname'),
      price: cartItem.getAttribute('data-price'),
      inv: cartItem.getAttribute('data-inv'),
      image: imageSrc,
      imageAlt: imageAlt,
      imageAspectRatio: imageAspectRatio,
      url: itemNameElement ? itemNameElement.href : '',
      title: itemName,
      options: [...cartItem.querySelectorAll('.product-option')].map(option => {
        let dtElement = option.querySelector('dt');
        let ddElement = option.querySelector('dd');
        return {
          name: dtElement ? dtElement.textContent.trim() : '',
          value: ddElement ? ddElement.textContent.trim() : ''
        };
      }),
      discounts: [...cartItem.querySelectorAll('.discounts__discount')].map(discount => discount.textContent.trim()),
      quantity: cartItem.querySelector('.quantity__input') ? cartItem.querySelector('.quantity__input').value : 1,
      originalLinePrice: originalLinePrice,
      linePrice: originalLinePrice,
      subscription: false,
      subscriptionName: null
    };
  });
  cartLog('captureCartItems() done', { keys: Object.keys(cartItemData) });
}

let quantitySelector = ``;
let productType;

// Function to recreate the HTML for <cart-items> based on the updated object
function renderCartItems() {
  cartGroup('renderCartItems()', () => {
    let cartItemsContainer = document.querySelector('cart-items');
    if (!cartItemsContainer) return;

    cartItemsContainer.innerHTML = '';

    let keys = Object.keys(cartItemData);

    keys.sort((a, b) => {
      let priceA = parseFloat(cartItemData[a].linePrice || cartItemData[a].originalLinePrice || 0);
      let priceB = parseFloat(cartItemData[b].linePrice || cartItemData[b].originalLinePrice || 0);
      const freeA = priceA <= 0.01;
      const freeB = priceB <= 0.01;
      return (freeA ? 1 : 0) - (freeB ? 1 : 0);
    });

    cartLog('Rendering lines', keys.map(k => ({
      key: k,
      variantId: cartItemData[k]?.variantId,
      qty: cartItemData[k]?.quantity,
      subscription: cartItemData[k]?.subscription
    })));

    for (let i = 0; i < keys.length; i++) {
      const lineKey = keys[i];
      const item = cartItemData[lineKey];
      const index = i + 1;

      let featuredImage = item.featured_image || {
        url: item.image,
        alt: item.imageAlt,
        aspect_ratio: item.imageAspectRatio || 1
      };

      let linePrice = parseFloat(item.linePrice || item.originalLinePrice || 0);
      let originalLinePrice = parseFloat(item.originalLinePrice || 0);

      if (linePrice <= 0.01) {
        quantitySelector = `<div class=\"lock-quantity\"><strong style=\"display:none;\">${item.quantity}</strong></div>`;
        productType = 'free-product';
      } else {
        quantitySelector = `
        <div class=\"cart-item__quantity\">
          <div class=\"cart-item__quantity-wrapper\">
            <label class=\"visually-hidden\" for=\"Quantity-${index}\">Quantity</label>
            <quantity-input class=\"quantity\">
              <button class=\"quantity_btn btn-minus\" tabindex=\"0\" name=\"minus\" type=\"button\">-</button>
              <input class=\"quantity__input\" type=\"number\" name=\"updates[]\" value=\"${item.quantity}\" min=\"0\" aria-label=\"${item.quantity} item of ${item.title} in cart.\" id=\"Quantity-${index}\" data-index=\"${index}\" readonly>
              <button class=\"quantity_btn btn-plus\" tabindex=\"0\" name=\"plus\" type=\"button\">+</button>
            </quantity-input>
          </div>
        </div>`;
        productType = 'standard-product';
      }

      let subscriptionLabelHtml = '';
      if (item.subscription && item.subscriptionName) {
        subscriptionLabelHtml = `
          <div class=\"product-option subscription-label\">
            Subscription: ${item.subscriptionName}
          </div>`;
      } else {
        subscriptionLabelHtml = `
          <div class=\"product-option subscription-label\">
            One-time purchase
          </div>`;
      }

      let priceHtml = '';
      if (item.subscription) {
        priceHtml = `
          <span class=\"item-price--current\">$${linePrice.toFixed(2)}</span>
          ${originalLinePrice && originalLinePrice > linePrice
            ? `<span class=\"item-price--compare\">$${originalLinePrice.toFixed(2)}</span>`
            : ''}`;
      } else {
        priceHtml = `$${linePrice.toFixed(2)}`;
      }

      let cartItemHTML = `
        <div class=\"cart-item ${productType}\"
          data-num=\"${index}\"
          id=\"${item.id}\"
          data-key=\"${lineKey}\"
          data-id=\"${item.variantId}\"
          data-shortname=\"${item.shortname}\"
          data-price=\"${item.price}\"
          data-inv=\"${item.inv}\">
          <div class=\"item-image\">
            <img src=\"${featuredImage.url}&width=200\" class=\"cart-img\" alt=\"${featuredImage.alt}\" loading=\"lazy\" width=\"150\" height=\"${Math.ceil(200 / featuredImage.aspect_ratio)}\">
          </div>
          <div class=\"item-details\">
            <div class=\"item-contain\">
              <a href=\"${item.url}\" class=\"item-name\">${String(item.title || '').toLowerCase()}</a>
              <div class=\"item-qualities\">
                <dl class=\"item-options\">
                  ${(item.options || [])
          .filter(option => {
            const v = String(option?.value || '').trim().toLowerCase();
            return v && v !== 'default title';
          })
          .map(option => `
    <div class="product-option">
      <dt><span class="visually-hidden">${option.name}</span><span class="sep"> / </span></dt>
      <dd>${option.value}</dd>
    </div>`)
          .join('')}
                </dl>
                ${subscriptionLabelHtml}
                <ul class=\"discounts list-unstyled\" role=\"list\">
                  ${item.discounts.map(discount => `<li class=\"discounts__discount\">${discount}</li>`).join('')}
                </ul>
              </div>
              ${quantitySelector}
            </div>
          </div>
          <div class=\"item-totals\">
            <div class=\"item-price\">
              ${priceHtml}
            </div>
            <div class=\"item-price-loading\" style=\"display:none;\">
              <img width=\"30\" height=\"30\" class=\"icon-loading\" loading=\"lazy\" src=\"https://cdn.shopify.com/s/files/1/2559/9416/files/ico-loading.gif?v=1720224887\" alt=\"Loading\" />
            </div>
            <div class=\"item-error\"></div>
            <div class=\"remove-this-item\" id=\"Remove-${index}\" data-index=\"${index}\">
              <span class=\"remove-btn\" aria-label=\"Remove ${item.title}\">Remove</span>
            </div>
          </div>
        </div>`;

      cartItemsContainer.insertAdjacentHTML('beforeend', cartItemHTML);
    }
  });
}

// Function to update the cart
const updateCart = debounce((lineKey, requestedQuantity) => {
  cartGroup(`updateCart(lineKey=${lineKey}, qty=${requestedQuantity})`, () => {
    cartLog('Sending /cart/change.js', { id: lineKey, quantity: requestedQuantity });
  });

  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: requestedQuantity })
  })
    .then(async response => {
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`change.js failed (${response.status}) ${text}`);
      }
      return response.json().catch(() => null);
    })
    .then(result => {
      cartLog('change.js result', result);
      return fetchCartData();
    })
    .then(cart => {
      if (!cart) return;
      updateHeaderCartCount(cart.item_count);

      // Validate requested quantity vs actual for this line
      const updatedLine = cart.items.find(i => i.key === lineKey);
      const updatedQty = updatedLine ? updatedLine.quantity : 0;

      const cartItemEl = document.querySelector(`.cart-item[data-key=\"${CSS.escape(lineKey)}\"]`);
      if (cartItemEl) {
        const itemError = cartItemEl.querySelector('.item-error');
        if (itemError) {
          if (requestedQuantity > updatedQty) {
            itemError.textContent = "Sorry! That's all we have available.";
            itemError.style.display = 'block';
          } else {
            itemError.textContent = '';
            itemError.style.display = 'none';
          }
        }
      }
    })
    .catch(error => console.error('[CartDebug] updateCart error:', error));
}, 300);

// Swiper - Collection
var collectionSwiper = new Swiper(".collectionSwiper", {
  navigation: {
    nextEl: '.collectionSwiper-next',
    prevEl: '.collectionSwiper-prev'
  },
  // Default parameters
  loop: true,
  slidesPerView: 'auto',
  spaceBetween: 20,
  slidesPerGroupAuto: false,
  centeredSlides: true,
  noSwipingSelector: '.btn',
  watchSlidesVisibility: true,
  watchOverflow: true,
  allowTouchMove: false
});

// Desktop Product Swiper
$('.desktopProductSwiper .gallery-thumbs .product__media:first-child').addClass('selected');
$('.desktopProductSwiper .gallery-thumbs .product__media').on('click', function () {
  var id = $(this).data('id');
  $('.desktopProductSwiper .gallery-viewer .product__media').hide();
  $('.desktopProductSwiper .gallery-viewer .product__media[data-id="' + id + '"]').show();
  $('.desktopProductSwiper .gallery-thumbs .product__media').removeClass('selected');
  $(this).addClass('selected');
});

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

});

// Debounce function (kept if something else relies on it later, but we already defined one above)
function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        try {
          const result = fn.apply(this, args);
          if (result instanceof Promise) {
            result.then(resolve).catch(reject);
          } else {
            resolve(result);
          }
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
}

// General function to clear all items from the cart
async function clearCart() {
  try {
    const data = { updates: {} };

    // Set each cart item's quantity to 0
    const cart = await fetch('/cart.js').then(response => response.json());
    cart.items.forEach(item => {
      data.updates[item.id] = 0;
    });

    // Make request to update the cart
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to clear the cart');
    }

    console.log('Cart cleared successfully');

    // Remove all .cart-item elements inside cart-items
    document.querySelectorAll('cart-items .cart-item').forEach(item => item.remove());
    console.log(response);

    fetchCartData();

  } catch (error) {
    console.error('Error clearing the cart:', error);
  }
}

function mobileMenuHandler(event) {
  // Only process click events or keydown events where Enter was pressed
  if (event.type === 'click' || (event.type === 'keydown' && event.key === 'Enter')) {

  }
}

document.addEventListener('DOMContentLoaded', function () {

  cartLog('DOMContentLoaded: cart handlers init');
  captureCartItems();

  // Quantity + / -
  document.addEventListener('click', function (e) {
    if (e.target.closest('.quantity_btn')) {
      e.preventDefault();
      const cartItem = e.target.closest('.cart-item');
      if (!cartItem) return;

      const itemPrice = cartItem.querySelector('.item-price');
      const itemPriceLoading = cartItem.querySelector('.item-price-loading');

      const lineKey = cartItem.dataset.key;
      const variantId = cartItem.dataset.id;
      const variantInv = cartItem.dataset.inv;

      let orr_quantity = parseInt(cartItem.querySelector('.quantity__input').value, 10);
      let quantity = orr_quantity;

      cartGroup('Quantity click', () => {
        cartLog('Line', { lineKey, variantId, orr_quantity, variantInv });
      });

      // Show loading indicator and hide item price
      if (itemPrice) itemPrice.style.display = 'none';
      if (itemPriceLoading) itemPriceLoading.style.display = 'block';

      if (e.target.closest('.btn-plus')) {
        if (variantInv === 'undefined' || Number(variantInv) > orr_quantity) {
          quantity = orr_quantity + 1;
          const err = cartItem.querySelector('.item-error');
          if (err) err.textContent = '';
        } else {
          const err = cartItem.querySelector('.item-error');
          if (err) err.textContent = 'Not enough inventory available.';
          quantity = orr_quantity;
        }
      } else if (e.target.closest('.btn-minus') && orr_quantity > 1) {
        quantity = orr_quantity - 1;
        const err = cartItem.querySelector('.item-error');
        if (err) err.textContent = '';
      }

      cartLog('Qty decision', { orr_quantity, quantity });

      if (!lineKey) {
        console.error('[CartDebug] Missing data-key on cart item; cannot update safely.', cartItem);
        if (itemPrice) itemPrice.style.display = 'block';
        if (itemPriceLoading) itemPriceLoading.style.display = 'none';
        return;
      }

      if (quantity !== orr_quantity) {
        updateCart(lineKey, quantity).finally(() => {
          if (itemPrice) itemPrice.style.display = 'block';
          if (itemPriceLoading) itemPriceLoading.style.display = 'none';
        });
      } else {
        if (itemPrice) itemPrice.style.display = 'block';
        if (itemPriceLoading) itemPriceLoading.style.display = 'none';
      }
    }
  });

  // Remove This Item (remove by line key)
  document.addEventListener('click', async (e) => {
    const removeButton = e.target.closest('.remove-this-item');
    if (!removeButton) return;

    const cartItem = removeButton.closest('.cart-item');
    if (!cartItem) return;

    const lineKey = cartItem.dataset.key;
    const variantId = cartItem.dataset.id;

    cartGroup('Remove click', () => {
      cartLog('Removing line', { lineKey, variantId });
    });

    if (!lineKey) {
      console.error('[CartDebug] Missing data-key on cart item; cannot remove safely.', cartItem);
      return;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`remove failed (${response.status}) ${text}`);
      }

      cartLog('Remove success for lineKey', lineKey);

      delete cartItemData[lineKey];
      renderCartItems();
      fetchCartData();

    } catch (error) {
      console.error('[CartDebug] Error removing item from cart:', error);
    }
  });

  fetchCartData();
});

class MiniCartManager {
  constructor() {
    this.minicartSection = document.querySelector('#shopify-section-ocs-minicart');
    this.minicart = document.querySelector('#ocs-minicart');
    this.toggleButtons = document.querySelectorAll('.toggle-minicart');
    this.body = document.body;

    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    this.init();
  }

  init() {
    this.setInitialState();
    this.addToggleListeners();
  }

  setInitialState() {
    this.minicart.setAttribute('aria-hidden', 'true');
    this.minicartSection.classList.remove('open');
    this.body.classList.remove('noscroll');
  }

  setFocusTrap() {
    this.focusableElements = Array.from(
      this.minicart.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
      )
    );

    if (this.focusableElements.length > 0) {
      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    }
  }

  handleFocusTrap(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  openMiniCart() {
    this.minicartSection.classList.add('open');
    this.minicart.setAttribute('aria-hidden', 'false');
    this.body.classList.add('noscroll');

    this.setFocusTrap();
    this.firstFocusableElement?.focus();

    document.addEventListener('keydown', this.handleFocusTrap.bind(this));
  }

  closeMiniCart() {
    this.minicartSection.classList.remove('open');
    this.minicart.setAttribute('aria-hidden', 'true');
    this.body.classList.remove('noscroll');

    this.toggleButtons[0]?.focus();

    document.removeEventListener('keydown', this.handleFocusTrap.bind(this));
  }

  toggleMiniCart() {
    const isOpen = this.minicart.getAttribute('aria-hidden') === 'false';
    isOpen ? this.closeMiniCart() : this.openMiniCart();
  }

  addToggleListeners() {
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', () => this.toggleMiniCart());

      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleMiniCart();
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.minicart.getAttribute('aria-hidden') === 'false') {
        this.closeMiniCart();
      }
    });
  }
}

// Initialize the MiniCartManager
document.addEventListener('DOMContentLoaded', () => {
  window.miniCartManager = new MiniCartManager();
});

/* HEADER MANAGER */
class StickyHeaderManager {
  constructor(headerSelector, mainContentSelector) {
    this.header = document.querySelector(headerSelector);
    this.mainContent = document.querySelector(mainContentSelector);

    if (!this.header || !this.mainContent) {
      console.error('Header or Main Content element not found!');
      return;
    }

    this.init();
  }

  init() {
    this.setHeaderBehavior();
    window.addEventListener('scroll', () => this.handleScroll());
  }

  setHeaderBehavior() {
    const firstSection = this.mainContent.querySelector('section');
    this.hasHeroVideo = firstSection && firstSection.classList.contains('ocs-hero-video');

    if (this.hasHeroVideo) {
      this.header.classList.add('transparent');
    } else {
      document.body.classList.add('not-hero-page');
    }
  }

  handleScroll() {
    const scrollPosition = window.scrollY;

    if (this.hasHeroVideo) {
      if (scrollPosition > 100) {
        this.header.classList.remove('transparent');
      } else {
        this.header.classList.add('transparent');
      }
    }
  }
}

// Initialize the StickyHeaderManager
document.addEventListener('DOMContentLoaded', function () {
  new StickyHeaderManager('.header-combo', '#MainContent');
});

/* SEARCH DRAWER */
class SearchDrawerManager {
  constructor() {
    this.searchDrawer = document.querySelector('.search-drawer');
    this.openSearchButton = document.querySelector('.toggle-search.open-search');
    this.closeSearchButton = document.querySelector('.toggle-search.close-search');
    this.body = document.body;

    this.init();
  }

  init() {
    this.setInitialState();
    this.addToggleListeners();
  }

  setInitialState() {
    this.searchDrawer.setAttribute('aria-hidden', 'true');
    this.searchDrawer.hidden = true;
    this.closeSearchButton.style.display = 'none';
  }

  openSearchDrawer() {
    this.searchDrawer.hidden = false;
    this.searchDrawer.setAttribute('aria-hidden', 'false');
    this.searchDrawer.setAttribute('open', '');
    this.openSearchButton.style.display = 'none';
    this.closeSearchButton.style.display = 'block';
    this.body.classList.add('noscroll');
    this.searchDrawer.querySelector('.search-form-input').focus();
  }

  closeSearchDrawer() {
    this.searchDrawer.hidden = true;
    this.searchDrawer.setAttribute('aria-hidden', 'true');
    this.searchDrawer.removeAttribute('open');
    this.openSearchButton.style.display = 'block';
    this.closeSearchButton.style.display = 'none';
    this.body.classList.remove('noscroll');
    this.openSearchButton.focus();
  }

  toggleSearchDrawer() {
    const isDrawerOpen = this.searchDrawer.getAttribute('aria-hidden') === 'false';
    isDrawerOpen ? this.closeSearchDrawer() : this.openSearchDrawer();
  }

  addToggleListeners() {
    this.openSearchButton.addEventListener('click', () => this.toggleSearchDrawer());
    this.closeSearchButton.addEventListener('click', () => this.toggleSearchDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchDrawer.getAttribute('aria-hidden') === 'false') {
        this.closeSearchDrawer();
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SearchDrawerManager();
});

class HeaderManager {
  constructor() {
    this.mobileToggleOn = document.querySelector('.mobile-toggle-on');
    this.mobileToggleOff = document.querySelector('.mobile-toggle-off');
    this.mobileMenu = document.querySelector('#mobile-menu');
    this.body = document.body;
    this.levelOneItems = document.querySelectorAll('.level-one');
    this.submenus = document.querySelectorAll('.submenu-slide');

    this.init();
  }

  init() {
    this.setInitialMenuState();
    this.disableAllTabbing(this.mobileMenu);
    this.addMobileMenuListeners();
    this.addLevelOneListeners();
    this.addBackLinkListeners();
  }

  setInitialMenuState() {
    this.mobileMenu.hidden = true;
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    this.mobileToggleOn.setAttribute('aria-expanded', 'false');
  }

  disableTabbing(container) {
    container.querySelectorAll('a, button, input, select, textarea').forEach(el => {
      el.setAttribute('tabindex', '-1');
    });
  }

  enableTabbing(container) {
    container.querySelectorAll('a, button, input, select, textarea').forEach(el => {
      el.removeAttribute('tabindex');
    });
  }

  disableAllTabbing(container) {
    this.disableTabbing(container);
    this.submenus.forEach(submenu => {
      submenu.setAttribute('aria-hidden', 'true');
      submenu.style.left = '-10000px';
      this.disableTabbing(submenu);
    });
  }

  openMobileMenu() {
    this.mobileMenu.hidden = false;
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.mobileMenu.setAttribute('open', '');
    this.mobileToggleOn.setAttribute('aria-expanded', 'true');
    this.body.classList.add('noscroll');

    this.enableTabbing(this.mobileMenu);
    this.mobileMenu.querySelector('a, button, input').focus();
  }

  closeMobileMenu() {
    this.mobileMenu.hidden = true;
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    this.mobileMenu.removeAttribute('open');
    this.mobileToggleOn.setAttribute('aria-expanded', 'false');
    this.body.classList.remove('noscroll');

    this.disableAllTabbing(this.mobileMenu);
    this.mobileToggleOn.focus();
  }

  openSubmenu(submenu) {
    submenu.setAttribute('aria-hidden', 'false');
    submenu.style.left = '0';
    this.enableTabbing(submenu);
    submenu.querySelector('a, button').focus();
  }

  closeSubmenu(submenu) {
    submenu.setAttribute('aria-hidden', 'true');
    submenu.style.left = '-10000px';
    this.disableTabbing(submenu);
  }

  addMobileMenuListeners() {
    this.mobileToggleOn.addEventListener('click', () => this.openMobileMenu());
    this.mobileToggleOff.addEventListener('click', () => this.closeMobileMenu());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.mobileMenu.getAttribute('aria-hidden') === 'false') {
        this.closeMobileMenu();
      }
    });
  }

  addLevelOneListeners() {
    this.levelOneItems.forEach(item => {
      item.addEventListener('click', (e) => {
        this.handleLevelOneInteraction(e, item);
      });

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleLevelOneInteraction(e, item);
        }
      });
    });
  }

  handleLevelOneInteraction(e, item) {
    const submenu = item.nextElementSibling;

    if (submenu && submenu.classList.contains('submenu-slide')) {
      const isSubmenuHidden = submenu.getAttribute('aria-hidden') === 'true';

      if (isSubmenuHidden) {
        this.openSubmenu(submenu);
      } else {
        this.closeSubmenu(submenu);
      }
    }
  }

  addBackLinkListeners() {
    this.submenus.forEach(submenu => {
      const backLink = submenu.querySelector('.back-link');

      if (backLink) {
        backLink.addEventListener('click', () => {
          this.closeSubmenu(submenu);
          this.focusParentSubmenu(submenu);
        });

        backLink.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.closeSubmenu(submenu);
            this.focusParentSubmenu(submenu);
          }
        });
      }
    });
  }

  focusParentSubmenu(submenu) {
    const parent = submenu.closest('.submenu-slide')?.previousElementSibling || submenu.closest('.submenu-slide')?.parentElement;

    if (parent) {
      const parentLevelOneLink = parent.querySelector('.level-one, .level-two');
      if (parentLevelOneLink) {
        parentLevelOneLink.focus();
      }
    }
  }

  addNestedSubmenuListeners() {
    document.querySelectorAll('.level-two').forEach(levelTwoItem => {
      levelTwoItem.addEventListener('click', (e) => {
        const nestedSubmenu = levelTwoItem.nextElementSibling;

        if (nestedSubmenu && nestedSubmenu.classList.contains('submenu-slide')) {
          this.openSubmenu(nestedSubmenu);
        }
      });

      levelTwoItem.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const nestedSubmenu = levelTwoItem.nextElementSibling;

          if (nestedSubmenu && nestedSubmenu.classList.contains('submenu-slide')) {
            this.openSubmenu(nestedSubmenu);
          }
        }
      });
    });
  }

  addAnchorLinkListeners() {
    this.mobileMenu.querySelectorAll('a').forEach(link => {
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          if (!link.nextElementSibling || !link.nextElementSibling.classList.contains('submenu-slide')) {
            e.preventDefault();
            window.location.href = link.href;
          }
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const headerManager = new HeaderManager();
  headerManager.addNestedSubmenuListeners();
  headerManager.addAnchorLinkListeners();
});

class ProductCard {
  constructor(productCardElement) {
    this.productCardElement = productCardElement;

    const addToCartButton = productCardElement.querySelector('.add-to-cart');
    if (!addToCartButton) {
      console.warn('Skipping product card with no add-to-cart button:', productCardElement);
      return;
    }

    this.currentVarID = addToCartButton.getAttribute('data-id');
    this.init();
  }

  init() {
    this.attachEventListeners();
  }

  attachEventListeners() {
    const addToCartButton = this.productCardElement.querySelector('.add-to-cart');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', (event) => this.addToCart(event));
    }
  }

  async addToCart(event) {
    event.preventDefault();
    console.log(`Adding product variant ID: ${this.currentVarID}`);

    try {
      const itemsToAdd = [
        {
          id: this.currentVarID,
          quantity: 1,
        },
      ];

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ items: itemsToAdd }),
      });

      if (!addResponse.ok) {
        const error = await addResponse.json();
        throw new Error(error.message);
      }

      const data = await addResponse.json();
      console.log("Product added to cart:", data);

      // Update mini cart and toggle it open
      this.updateMiniCart(data);
      this.toggleMiniCart(true);

      // Update the header cart count
      const updatedCart = await fetchCartData();
      updateHeaderCartCount(updatedCart.item_count);

    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  }

  updateMiniCart(cart) {
    if (typeof fetchCartData === "function") {
      fetchCartData();
    } else {
      console.error('fetchCartData function is not defined');
    }
  }

  toggleMiniCart(open) {
    const minicartSection = document.getElementById('shopify-section-ocs-minicart');
    if (minicartSection) {
      if (open) {
        minicartSection.classList.add('open');
        document.body.classList.add('noscroll');
      } else {
        minicartSection.classList.remove('open');
        document.body.classList.remove('noscroll');
      }
    } else {
      console.warn('Mini cart section not found.');
    }
  }
}

// Initialize ProductCard instances for all product cards on the page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-item').forEach(card => {
    new ProductCard(card);
  });
});